import { create } from 'zustand'
import type { AuthResponse, PlayerProfile } from '@/gen/player/v1/player_pb'
import type { SessionEndReason } from './refresh'

/**
 * The web session. IN MEMORY ONLY — never persisted.
 *
 * Firebase keeps its own browser session, so a reload re-exchanges a fresh
 * Firebase ID token for new MafiaGo tokens (WebSession.SignIn) instead of
 * storing a refresh token where any injected script could read it.
 */
export type SessionStatus = 'restoring' | 'signedOut' | 'signedIn'

export interface SessionState {
  status: SessionStatus
  accessToken: string | null
  refreshToken: string | null
  /** The profile from the last sign-in/refresh; feeds the top bar until GetPlayer answers. */
  player: PlayerProfile | null
  /** Signed in through the mock transport ("demo mode"). */
  demo: boolean
  /** When the current access token was issued (client clock), for the proactive refresh. */
  issuedAt: number | null
  /** Why the last session ended, when the login screen should say so. */
  endedReason: SessionEndReason | null
  setAuth: (res: AuthResponse, opts?: { demo?: boolean }) => void
  setPlayer: (player: PlayerProfile) => void
  setStatus: (status: SessionStatus) => void
  clear: (reason?: SessionEndReason) => void
}

export const useSession = create<SessionState>()((set) => ({
  status: 'restoring',
  accessToken: null,
  refreshToken: null,
  player: null,
  demo: false,
  issuedAt: null,
  endedReason: null,
  setAuth: (res, opts) =>
    set((s) => ({
      status: 'signedIn',
      endedReason: null,
      accessToken: res.accessToken,
      // A Refresh that rotates returns a new refresh token; keep the old one
      // only if the server sent none.
      refreshToken: res.refreshToken || s.refreshToken,
      player: res.player ?? s.player,
      demo: opts?.demo ?? s.demo,
      issuedAt: Date.now(),
    })),
  setPlayer: (player) => set({ player }),
  setStatus: (status) => set({ status }),
  clear: (reason) =>
    set({
      status: 'signedOut',
      accessToken: null,
      refreshToken: null,
      player: null,
      demo: false,
      issuedAt: null,
      endedReason: reason ?? null,
    }),
}))

/** The signed-in player's id, read at call time (never cached across sessions). */
export function selfPlayerId(): string | null {
  return useSession.getState().player?.playerId ?? null
}

export function useSelfId(): string | null {
  return useSession((s) => s.player?.playerId ?? null)
}
